import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Icon } from '@iconify/react';
import { useNavigate } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import hebergementService from "../services/hebergementService";
import secteursService from "../services/secteursService";
import SiteTypeInlineCell from "../components/iti/SiteTypeInlineCell";
import SiteFonctionnalitesInlineCell from "../components/iti/SiteFonctionnalitesInlineCell";
import SiteClientInlineCell from "../components/iti/SiteClientInlineCell";
import SitesWebSelect from '../components/iti/SitesWebSelect';
import SitesWebStickyHead from '../components/iti/SitesWebStickyHead';
import KkpClientAutocomplete from '../components/kkp/KkpClientAutocomplete';
import { AppAlert, AppButton } from '../components/ui';
import "../styles/sites-web.scss";

function formatHostingDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('fr-FR');
}

function formatHostingEuro(value) {
  if (value == null || value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '';
  return amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function hostingOrderKey(siteInfo) {
  if (!siteInfo.hostingOrder?.status) return 'none';
  return siteInfo.hostingOrder.status;
}

function hostingOrderYear(siteInfo) {
  const date = siteInfo.hostingOrder?.date;
  if (typeof date !== 'string' || date.length < 4) return '';
  return date.slice(0, 4);
}

function flattenGroupOptions(groups) {
  return (groups || []).flatMap((group) => (group.options ?? []).map((opt) => ({
    value: String(opt.value ?? opt.id ?? ''),
    label: opt.label ?? opt.libelle ?? String(opt.id ?? ''),
  }))).filter((opt) => opt.value);
}

function serverCode(name) {
  const match = String(name || '').match(/^(s\d+)/i);
  return match ? match[1].toLowerCase() : (name || '—');
}

function HostingOrderCell({ siteInfo }) {
  const order = siteInfo.hostingOrder;
  if (!order) {
    return <span className="sw-none">Aucune</span>;
  }

  const dateLabel = formatHostingDate(order.date);
  const amount = formatHostingEuro(order.totalTtc);
  const title = [order.productLabel, amount].filter(Boolean).join(' · ');
  const paid = order.status === 'paid';
  const label = paid ? 'Payée' : (order.status === 'unpaid' ? 'À régler' : order.statusLabel);

  return (
    <a
      href={order.url || undefined}
      target={order.url ? '_blank' : undefined}
      rel={order.url ? 'noopener noreferrer' : undefined}
      className="sw-invoice"
      title={title || undefined}
      onClick={(event) => {
        if (!order.url) event.preventDefault();
      }}
    >
      <span className="sw-invoice__ref">{order.ref || dateLabel || 'Facture'}</span>
      <span className="sw-invoice__meta">
        {order.ref && dateLabel ? dateLabel : null}
        {label ? <span className={`sw-pay ${paid ? 'sw-pay--paid' : 'sw-pay--due'}`}>{label}</span> : null}
      </span>
    </a>
  );
}

const SiteWebPage = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [sortedSites, setSortedSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [linkTab, setLinkTab] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortField] = useState('name');
  const [sortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    status: [],
    server: [],
    sectionActivite: [],
    typeSite: [],
    fonctionnalites: [],
    commercial: [],
    hostingOrder: [],
    hostingOrderYear: [],
    dolibarrClientId: null,
    dolibarrClientLabel: '',
    showFilters: false
  });
  const [secteursActiviteGroups, setSecteursActiviteGroups] = useState([]);
  const [typesSiteGroups, setTypesSiteGroups] = useState([]);
  const [fonctionnalitesGroups, setFonctionnalitesGroups] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [autoLinking, setAutoLinking] = useState(false);
  const [autoLinkMessage, setAutoLinkMessage] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [apiWorking, setApiWorking] = useState(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Mapping des IP vers les noms de serveurs
  const getServerName = (ip) => {
    const serverMapping = {
      '151.80.20.230': 's1.iti-conseil.com',
      '188.165.192.156': 's2.iti-conseil.com',
      '51.210.221.102': 's4.iti-conseil.com',
      '146.59.148.37': 's5.iti-conseil.com'
    };
    return serverMapping[ip] || ip;
  };

  // Actif = encore présent dans MainWP, donc encore hébergé.
  const getSiteStatus = (site) => {
    if (site.hosted === true) return 'active';
    if (site.hosted === false) return 'inactive';
    return 'unknown';
  };

  // Fonction pour obtenir le texte du statut
  const getStatusText = (status) => {
    const statusTexts = {
      'active': 'Hébergé',
      'inactive': 'Plus hébergé',
      'error': 'Erreur',
      'unknown': 'Inconnu'
    };
    return statusTexts[status] || status;
  };

  // Fonction de tri
  const sortSites = useCallback((sitesList, field, direction) => {
    return [...sitesList].sort((a, b) => {
      let aValue, bValue;

      switch (field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = getSiteStatus(a);
          bValue = getSiteStatus(b);
          break;
        case 'url':
          aValue = a.url.toLowerCase();
          bValue = b.url.toLowerCase();
          break;
        case 'ip':
          aValue = (a.ip || '').toLowerCase();
          bValue = (b.ip || '').toLowerCase();
          break;
        case 'sectionActivite':
          aValue = (a.secteurActiviteLabel || a.sectionActiviteLabel || '').toLowerCase();
          bValue = (b.secteurActiviteLabel || b.sectionActiviteLabel || '').toLowerCase();
          break;
        case 'typeSite':
          aValue = (a.typeSiteLabel || '').toLowerCase();
          bValue = (b.typeSiteLabel || '').toLowerCase();
          break;
        case 'fonctionnalites':
          aValue = (a.fonctionnaliteLabels || []).join(', ').toLowerCase();
          bValue = (b.fonctionnaliteLabels || []).join(', ').toLowerCase();
          break;
        case 'client':
          aValue = (a.clientNom || '').toLowerCase();
          bValue = (b.clientNom || '').toLowerCase();
          break;
        case 'hostingOrder':
          aValue = a.hostingOrder?.date || '';
          bValue = b.hostingOrder?.date || '';
          break;
        case 'commercial':
          aValue = (a.commercialNom || '').toLowerCase();
          bValue = (b.commercialNom || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, []);

  // Fonction pour appliquer les filtres
  const applyFilters = useCallback((sitesList) => {
    return sitesList.filter(siteInfo => {
      const linked = Boolean(siteInfo.dolibarrThirdpartyId || siteInfo.clientNom);
      if (linkTab === 'linked' && !linked) return false;
      if (linkTab === 'unlinked' && linked) return false;

      if (filters.status.length > 0) {
        const status = getSiteStatus(siteInfo);
        if (!filters.status.includes(status)) return false;
      }

      if (filters.server.length > 0) {
        const serverName = getServerName(siteInfo.ip);
        if (!filters.server.includes(serverName)) return false;
      }

      if (filters.sectionActivite.length > 0) {
        const section = siteInfo.sectionActivitePrincipale || '';
        if (!filters.sectionActivite.includes(section)) return false;
      }

      if (filters.typeSite.length > 0) {
        const typeSite = siteInfo.typeSitePrincipale || '';
        if (!filters.typeSite.includes(typeSite)) return false;
      }

      if (filters.fonctionnalites.length > 0) {
        const siteIds = (siteInfo.fonctionnaliteIds ?? []).map((id) => String(id));
        const hasMatch = filters.fonctionnalites.some((id) => siteIds.includes(String(id)));
        if (!hasMatch) return false;
      }

      if (filters.commercial.length > 0) {
        const commercialId = siteInfo.commercialId ? String(siteInfo.commercialId) : '';
        if (!filters.commercial.includes(commercialId)) return false;
      }

      if (filters.hostingOrder.length > 0) {
        const key = hostingOrderKey(siteInfo);
        if (!key || !filters.hostingOrder.includes(key)) return false;
      }

      if (filters.hostingOrderYear.length > 0) {
        const year = hostingOrderYear(siteInfo);
        if (!filters.hostingOrderYear.includes(year)) return false;
      }

      if (filters.dolibarrClientId == null) {
        const label = (filters.dolibarrClientLabel || '').trim().toLowerCase();
        if (label && !(siteInfo.clientNom || '').toLowerCase().includes(label)) return false;
      }

      if (filters.dolibarrClientId != null) {
        const filterId = Number(filters.dolibarrClientId);
        const filterNom = (filters.dolibarrClientLabel || '').trim().toLowerCase();
        const siteId = siteInfo.dolibarrThirdpartyId != null
          ? Number(siteInfo.dolibarrThirdpartyId)
          : null;
        const siteNom = (siteInfo.clientNom || '').trim().toLowerCase();

        if (siteId === filterId) {
          return true;
        }
        if (filterNom && siteNom && siteNom === filterNom) {
          return true;
        }

        return false;
      }

      return true;
    });
  }, [linkTab, filters.status, filters.server, filters.sectionActivite, filters.typeSite, filters.fonctionnalites, filters.commercial, filters.hostingOrder, filters.hostingOrderYear, filters.dolibarrClientId, filters.dolibarrClientLabel]);

  // Fonction pour mettre à jour les filtres
  const updateFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset à la première page lors d'un filtrage
  };

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      status: [],
      server: [],
      sectionActivite: [],
      typeSite: [],
      fonctionnalites: [],
      commercial: [],
      hostingOrder: [],
      hostingOrderYear: [],
      dolibarrClientId: null,
      dolibarrClientLabel: '',
      showFilters: false
    });
    setCurrentPage(1);
  };

  const handleSiteUpdated = useCallback((siteId, updatedSite) => {
    const merge = (list) => list.map((s) => (s.id === siteId ? { ...s, ...updatedSite } : s));
    setSites(merge);
    setFilteredSites(merge);
  }, []);

  const commercialOptions = useMemo(
    () => commerciaux.map((c) => ({
      value: String(c.id),
      label: c.nom,
    })),
    [commerciaux],
  );

  const statusOptions = useMemo(() => [
    { value: 'active', label: 'Hébergé' },
    { value: 'inactive', label: 'Plus hébergé' },
    { value: 'unknown', label: 'Inconnu' },
  ], []);

  const hostingOrderOptions = useMemo(() => [
    { value: 'paid', label: 'Payée' },
    { value: 'unpaid', label: 'À régler' },
    { value: 'none', label: 'Aucune' },
  ], []);

  const invoiceYearOptions = useMemo(() => {
    const years = new Set();
    sites.forEach((site) => {
      const year = hostingOrderYear(site);
      if (year) years.add(year);
    });
    return [...years]
      .sort((a, b) => b.localeCompare(a))
      .map((year) => ({ value: year, label: year }));
  }, [sites]);

  const serverOptions = useMemo(() => [
    { value: 's1.iti-conseil.com', label: 's1' },
    { value: 's2.iti-conseil.com', label: 's2' },
    { value: 's4.iti-conseil.com', label: 's4' },
    { value: 's5.iti-conseil.com', label: 's5' },
  ], []);

  const sectorOptions = useMemo(
    () => flattenGroupOptions(secteursActiviteGroups),
    [secteursActiviteGroups],
  );
  const typeOptions = useMemo(
    () => flattenGroupOptions(typesSiteGroups),
    [typesSiteGroups],
  );
  const featureOptions = useMemo(
    () => flattenGroupOptions(fonctionnalitesGroups),
    [fonctionnalitesGroups],
  );

  const renderSiteParts = (siteInfo) => {
    const status = getSiteStatus(siteInfo);
    const statusClass = status === 'active' ? 'sw-status--on' : status === 'inactive' ? 'sw-status--off' : 'sw-status--unknown';
    const serverName = getServerName(siteInfo.ip);
    const letter = (siteInfo.name || '?').trim().charAt(0).toUpperCase();

    return {
      site: (
        <div className="sw-site">
          <span className="sw-letter">{letter}</span>
          <span className="sw-site__text">
            {siteInfo.url ? (
              <a className="sw-site__name" href={siteInfo.url} target="_blank" rel="noopener noreferrer">{siteInfo.name}</a>
            ) : (
              <span className="sw-site__name">{siteInfo.name}</span>
            )}
            <span className="sw-site__id">ID {siteInfo.mainwpId || siteInfo.id}</span>
          </span>
        </div>
      ),
      client: (
        <SiteClientInlineCell
          siteInfo={siteInfo}
          apiWorking={apiWorking}
          onUpdated={handleSiteUpdated}
          layout="sites"
        />
      ),
      status: (
        <span
          className={`sw-status ${statusClass}`}
          title={status === 'active' ? 'Encore dans MainWP' : status === 'inactive' ? 'Retiré de MainWP' : 'Présence MainWP pas encore importée'}
        >
          <span className="sw-status__dot" />
          {getStatusText(status)}
        </span>
      ),
      invoice: <HostingOrderCell siteInfo={siteInfo} />,
      server: <span className="sw-server" title={serverName || undefined}>{serverCode(serverName)}</span>,
      tags: (
        <div className="sw-tags">
          <SiteTypeInlineCell
            siteInfo={siteInfo}
            typesSiteGroups={typesSiteGroups}
            apiWorking={apiWorking}
            onUpdated={handleSiteUpdated}
          />
          <SiteFonctionnalitesInlineCell
            siteInfo={siteInfo}
            fonctionnalitesGroups={fonctionnalitesGroups}
            apiWorking={apiWorking}
            onUpdated={handleSiteUpdated}
          />
        </div>
      ),
      gear: (
        <AppButton
          className="sw-gear"
          icon="lucide:settings"
          iconWidth={15}
          onClick={() => navigate(`/iti/hebergement/sites/${siteInfo.id}`)}
          title="Réglages du site"
          aria-label="Réglages du site"
        />
      ),
    };
  };

  // Charger les options de filtres prospection
  useEffect(() => {
    let cancelled = false;

    const loadFilterOptions = async () => {
      try {
        const [filterData, commerciauxData] = await Promise.all([
          secteursService.getFilterOptions(),
          secteursService.getCommerciaux(),
        ]);
        if (!cancelled) {
          setSecteursActiviteGroups(filterData.secteursActivite ?? []);
          setTypesSiteGroups(filterData.typesSite ?? []);
          setFonctionnalitesGroups(filterData.fonctionnalites ?? []);
          setCommerciaux(commerciauxData ?? []);
        }
      } catch (err) {
        console.error('Erreur chargement filtres:', err);
      }
    };

    loadFilterOptions();
    return () => { cancelled = true; };
  }, []);

  // Charger les sites au montage du composant
  useEffect(() => {
    const loadSites = async () => {
      console.time('Site Loading');
      try {
        setLoading(true);
        setError(null);

        // Vérifier si l'utilisateur est connecté
        const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token') || localStorage.getItem('token');
        if (!token) {
          setError('Vous devez être connecté pour accéder à cette page');
          setLoading(false);
          return;
        }

        // Charger la liste des sites avec pagination
        try {
          const result = await hebergementService.getSites({ limit: 1000 });
          const sitesData = result.data.sites.map(site => ({
            ...site,
            error: null
          }));
        
          setSites(sitesData);
          setFilteredSites(sitesData);

        } catch (apiError) {
          console.error('Erreur API, utilisation de données de test:', apiError);
          setApiWorking(false);
          
          // Données de test en cas d'erreur API
          const testSites = [
            {
              id: 1,
              mainwpId: 'test-1',
              name: 'Site Test 1',
              url: 'https://test1.example.com',
              ip: '151.80.20.230',
              status: 'active',
              hosted: true,
              httpStatus: 200,
              lastSync: '2024-01-15T10:30:00Z',
              error: null
            },
            {
              id: 2,
              mainwpId: 'test-2',
              name: 'Site Test 2',
              url: 'https://test2.example.com',
              ip: '188.165.192.156',
              status: 'inactive',
              hosted: false,
              httpStatus: 404,
              lastSync: '2024-01-14T15:45:00Z',
              error: null
            },
            {
              id: 3,
              mainwpId: 'test-3',
              name: 'Site Test 3',
              url: 'https://test3.example.com',
              ip: '51.210.221.102',
              status: 'error',
              hosted: true,
              httpStatus: 500,
              lastSync: '2024-01-13T09:20:00Z',
              error: null
            }
          ];
          
          setSites(testSites);
          setFilteredSites(testSites);
        }

      } catch (err) {
        console.error('Erreur lors du chargement des sites:', err);
        setError(err.message || 'Erreur lors du chargement des sites');
      } finally {
        setLoading(false);
        console.timeEnd('Site Loading');
      }
    };

    loadSites();
  }, []);

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Filtrer les sites selon le terme de recherche et les filtres
  useEffect(() => {
    let filtered = sites;

    // Filtre par terme de recherche (utilise le terme debounced)
    if (debouncedSearchTerm) {
      filtered = filtered.filter(siteInfo => 
        siteInfo.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        siteInfo.url.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (siteInfo.ip && siteInfo.ip.includes(debouncedSearchTerm)) ||
        (siteInfo.entrepriseNom && siteInfo.entrepriseNom.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (siteInfo.clientNom && siteInfo.clientNom.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (siteInfo.commercialNom && siteInfo.commercialNom.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      );
    }

    // Appliquer les autres filtres
    filtered = applyFilters(filtered);

    setFilteredSites(filtered);
  }, [debouncedSearchTerm, sites, filters, applyFilters]);

  useEffect(() => {
    const total = Math.ceil(filteredSites.length / itemsPerPage) || 1;
    if (currentPage > total) {
      setCurrentPage(total);
    }
  }, [filteredSites.length, itemsPerPage, currentPage]);

  // Trier les sites filtrés
  useEffect(() => {
    const sorted = sortSites(filteredSites, sortField, sortDirection);
    setSortedSites(sorted);
  }, [filteredSites, sortField, sortDirection, sortSites]);

  // Fonction pour auto-lier via Dolibarr
  const handleAutoLinkAll = async () => {
    setAutoLinking(true);
    setAutoLinkMessage(null);
    try {
      const result = await hebergementService.autoLinkAllEntreprises(false);
      if (result.success) {
        const {
          linked_client: linkedClient = 0,
          linked_entreprise: linkedEntreprise = 0,
          linked_commercial: linkedCommercial = 0,
          linked_section: linkedSection = 0,
          linked_any: linkedAny = 0,
          linked_none: linkedNone = 0,
          total = 0,
        } = result.data;
        setAutoLinkMessage(
          `${linkedAny} site(s) enrichi(s) sur ${total} `
          + `(clients: ${linkedClient}, entreprises: ${linkedEntreprise}, `
          + `commerciaux: ${linkedCommercial}, secteurs: ${linkedSection}, `
          + `sans correspondance: ${linkedNone})`,
        );
        refreshData();
      }
    } catch (err) {
      setAutoLinkMessage(err.message || 'Erreur lors de l\'auto-liaison');
    } finally {
      setAutoLinking(false);
    }
  };

  // Fonction pour rafraîchir les données
  const refreshData = () => {
    const loadSites = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Charger la liste des sites avec pagination
        try {
          const result = await hebergementService.getSites({ limit: 10000 });
          const sitesData = result.data.sites.map(site => ({
            ...site,
            error: null
          }));
        
          setSites(sitesData);
          setFilteredSites(sitesData);

        } catch (apiError) {
          console.error('Erreur API, utilisation de données de test:', apiError);
          setApiWorking(false);
          
          // Données de test en cas d'erreur API
          const testSites = [
            {
              id: 1,
              mainwpId: 'test-1',
              name: 'Site Test 1',
              url: 'https://test1.example.com',
              ip: '151.80.20.230',
              status: 'active',
              hosted: true,
              httpStatus: 200,
              lastSync: '2024-01-15T10:30:00Z',
              error: null
            },
            {
              id: 2,
              mainwpId: 'test-2',
              name: 'Site Test 2',
              url: 'https://test2.example.com',
              ip: '188.165.192.156',
              status: 'inactive',
              hosted: false,
              httpStatus: 404,
              lastSync: '2024-01-14T15:45:00Z',
              error: null
            },
            {
              id: 3,
              mainwpId: 'test-3',
              name: 'Site Test 3',
              url: 'https://test3.example.com',
              ip: '51.210.221.102',
              status: 'error',
              hosted: true,
              httpStatus: 500,
              lastSync: '2024-01-13T09:20:00Z',
              error: null
            }
          ];
          
          setSites(testSites);
          setFilteredSites(testSites);
        }

      } catch (err) {
        console.error('Erreur lors du chargement des sites:', err);
        setError(err.message || 'Erreur lors du chargement des sites');
      } finally {
        setLoading(false);
      }
    };

    loadSites();
  };

  // Calculer la pagination (optimisé avec useMemo)
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(sortedSites.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSites = sortedSites.slice(startIndex, endIndex);
    
    return { totalPages, startIndex, endIndex, currentSites };
  }, [sortedSites, currentPage, itemsPerPage]);

  const { totalPages, startIndex, endIndex, currentSites } = paginationData;
  const rangeStart = sortedSites.length === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(endIndex, sortedSites.length);
  const pageCount = Math.max(totalPages, 1);

  const linkCounts = useMemo(() => {
    let linked = 0;
    sites.forEach((site) => {
      if (site.dolibarrThirdpartyId || site.clientNom) linked += 1;
    });
    return { all: sites.length, linked, unlinked: sites.length - linked };
  }, [sites]);

  const activeFilterCount = [
    filters.status,
    filters.server,
    filters.sectionActivite,
    filters.typeSite,
    filters.fonctionnalites,
    filters.commercial,
    filters.hostingOrder,
    filters.hostingOrderYear,
  ].filter((values) => values.length > 0).length
    + ((filters.dolibarrClientId != null || (filters.dolibarrClientLabel || '').trim()) ? 1 : 0);

  const selected = (key) => (filters[key] && filters[key][0] ? String(filters[key][0]) : '');
  const setSelected = (key, value) => updateFilter(key, value ? [value] : []);

  // Fonction pour changer de page
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const importFromMainWP = async () => {
    setSyncing(true);
    try {
      await hebergementService.importFromMainWP();
      refreshData();
    } catch (err) {
      console.error('Erreur lors de l\'import MainWP:', err);
      setError(err.message || 'Erreur lors de l\'import des sites depuis MainWP');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <MasterLayout>
      <div className="sites-web">
      <div className="sw-head">
        <div className="sw-head__titles">
          <h1 className="fw-semibold mb-0 app-page-title">Sites web</h1>
          <p>Tous les sites hébergés sur nos serveurs, avec leur client, leur facture d’hébergement et leurs fonctionnalités.</p>
        </div>
        <div className="sw-head__actions">
          <AppButton className="sw-btn" icon="lucide:refresh-cw" iconWidth={16} onClick={refreshData} disabled={loading} loading={loading} loadingLabel="Actualiser">
            Actualiser
          </AppButton>
          <AppButton className="sw-btn" icon="lucide:download-cloud" iconWidth={16} onClick={importFromMainWP} disabled={syncing || loading || !apiWorking} loading={syncing} loadingLabel="Importer depuis MainWP">
            Importer depuis MainWP
          </AppButton>
          <AppButton className="sw-btn sw-btn--dark" icon="lucide:link" iconWidth={16} onClick={handleAutoLinkAll} disabled={autoLinking || loading || !apiWorking} loading={autoLinking} loadingLabel="Relier automatiquement">
            Relier automatiquement
          </AppButton>
        </div>
      </div>

      {error && (
        <AppAlert className="sw-note">
          {error}
          <AppButton className="sw-btn" onClick={refreshData}>Réessayer</AppButton>
        </AppAlert>
      )}

      {!apiWorking && (
        <AppAlert variant="warning" className="sw-note">
          Mode test : l&apos;API n&apos;est pas accessible.
          <AppButton className="sw-btn" onClick={() => { setApiWorking(true); refreshData(); }}>
            Réessayer l&apos;API
          </AppButton>
        </AppAlert>
      )}

      {autoLinkMessage && (
        <AppAlert variant="info" className="sw-note" role="status">{autoLinkMessage}</AppAlert>
      )}

      <div className="sw-stats">
        {[
          { key: 'all', label: 'Tous les sites', count: linkCounts.all, hint: 'Sur l’ensemble des serveurs', dot: 'var(--sw-placeholder)' },
          { key: 'linked', label: 'Reliés à un client', count: linkCounts.linked, hint: 'Fiche client Dolibarr associée', dot: 'var(--sw-ok-dot)' },
          { key: 'unlinked', label: 'À relier', count: linkCounts.unlinked, hint: 'Aucun client associé pour l’instant', dot: '#e8823f' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`sw-stat${linkTab === tab.key ? ' is-on' : ''}`}
            onClick={() => { setLinkTab(tab.key); setCurrentPage(1); }}
          >
            <span className="sw-stat__label"><span className="sw-stat__dot" style={{ background: tab.dot }} />{tab.label}</span>
            <span className="sw-stat__count">{tab.count}</span>
            <span className="sw-stat__hint">{tab.hint}</span>
          </button>
        ))}
      </div>

      <div className="sw-panel">
        <div className="sw-search-row">
          <label className="sw-search">
            <Icon icon="lucide:search" width={17} />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Rechercher un site, un client, une adresse IP…"
            />
          </label>
          <AppButton className="sw-btn sw-filters-toggle" icon="lucide:sliders-horizontal" iconWidth={16} onClick={() => setFiltersOpen((open) => !open)}>
            {`Filtres${activeFilterCount ? ` (${activeFilterCount})` : ''}`}
          </AppButton>
          {activeFilterCount > 0 && (
            <button type="button" className="sw-clear" onClick={resetFilters}>
              <Icon icon="lucide:filter-x" width={16} />
              Effacer les filtres
            </button>
          )}
        </div>
        <div className={`sw-filters${filtersOpen ? ' is-open' : ''}`}>
          <SitesWebSelect label="Statut" value={selected('status')} onChange={(value) => setSelected('status', value)} options={statusOptions} placeholder="Tous" />
          <SitesWebSelect label="Serveur" value={selected('server')} onChange={(value) => setSelected('server', value)} options={serverOptions} placeholder="Tous" />
          <SitesWebSelect label="Secteur d’activité" value={selected('sectionActivite')} onChange={(value) => setSelected('sectionActivite', value)} options={sectorOptions} placeholder="Tous les secteurs" searchable={sectorOptions.length > 6} />
          <SitesWebSelect label="Type de site" value={selected('typeSite')} onChange={(value) => setSelected('typeSite', value)} options={typeOptions} placeholder="Tous les types" searchable={typeOptions.length > 6} />
          <SitesWebSelect label="Fonctionnalités" value={selected('fonctionnalites')} onChange={(value) => setSelected('fonctionnalites', value)} options={featureOptions} placeholder="Toutes" searchable={featureOptions.length > 6} />
          <SitesWebSelect label="Commercial" value={selected('commercial')} onChange={(value) => setSelected('commercial', value)} options={commercialOptions} placeholder="Tous" searchable={commercialOptions.length > 6} />
          <div className={`sw-field sw-client-filter${(filters.dolibarrClientLabel || '').trim() ? ' is-set' : ''}`}>
            Client Dolibarr
            <KkpClientAutocomplete
              value={filters.dolibarrClientLabel}
              dolibarrOnly
              placeholder="Rechercher un client…"
              onChange={(label) => {
                setFilters((prev) => ({ ...prev, dolibarrClientLabel: label, dolibarrClientId: null }));
                setCurrentPage(1);
              }}
              onSelect={(client) => {
                setFilters((prev) => ({
                  ...prev,
                  dolibarrClientId: client?.id != null ? Number(client.id) : null,
                  dolibarrClientLabel: client?.nom ?? '',
                }));
                setCurrentPage(1);
              }}
            />
          </div>
          <SitesWebSelect label="Facture hébergement" value={selected('hostingOrder')} onChange={(value) => setSelected('hostingOrder', value)} options={hostingOrderOptions} placeholder="Toutes" />
          <SitesWebSelect label="Année de facture" value={selected('hostingOrderYear')} onChange={(value) => setSelected('hostingOrderYear', value)} options={invoiceYearOptions} placeholder="Toutes" searchable={invoiceYearOptions.length > 8} />
        </div>
      </div>

      <div className="sw-board">
        {loading ? (
          <div className="sw-empty">Chargement des sites…</div>
        ) : currentSites.length === 0 ? (
          <div className="sw-empty">Aucun site trouvé</div>
        ) : (
          <>
            <div className="sw-desktop">
              <SitesWebStickyHead>
                <span>Site</span>
                <span>Client</span>
                <span>Statut</span>
                <span>Facture héb.</span>
                <span>Serveur</span>
                <span>Type et fonctionnalités</span>
                <span />
              </SitesWebStickyHead>
              {currentSites.map((siteInfo) => {
                const parts = renderSiteParts(siteInfo);
                return (
                  <div className="sw-grid sw-row" key={siteInfo.id}>
                    {parts.site}
                    {parts.client}
                    {parts.status}
                    {parts.invoice}
                    {parts.server}
                    {parts.tags}
                    {parts.gear}
                  </div>
                );
              })}
            </div>
            <div className="sw-cards">
              {currentSites.map((siteInfo) => {
                const parts = renderSiteParts(siteInfo);
                const serverName = getServerName(siteInfo.ip);
                const letter = (siteInfo.name || '?').trim().charAt(0).toUpperCase();
                return (
                  <div className="sw-card" key={siteInfo.id}>
                    <div className="sw-card__top">
                      <span className="sw-letter">{letter}</span>
                      <span className="sw-site__text">
                        {siteInfo.url ? (
                          <a className="sw-site__name" href={siteInfo.url} target="_blank" rel="noopener noreferrer">{siteInfo.name}</a>
                        ) : (
                          <span className="sw-site__name">{siteInfo.name}</span>
                        )}
                        <span className="sw-card__meta">
                          ID {siteInfo.mainwpId || siteInfo.id}
                          {' · '}
                          <span className="sw-card__srv">{serverCode(serverName)}</span>
                          {parts.status}
                        </span>
                      </span>
                      {parts.gear}
                    </div>
                    {parts.client}
                    <div className="sw-card__invoice">
                      <Icon icon="lucide:receipt" width={14} />
                      Facture hébergement
                      {parts.invoice}
                    </div>
                    {parts.tags}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {!loading && sortedSites.length > 0 && (
          <div className="sw-pager">
            <span className="sw-pager__count">{rangeStart} à {rangeEnd} sur {sortedSites.length} sites</span>
            <div className="sw-pager__nav">
              <AppButton className="sw-btn sw-btn--page" icon="lucide:chevron-left" iconWidth={15} onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>
                Précédent
              </AppButton>
              <span className="sw-pager__page">Page {currentPage} / {pageCount}</span>
              <AppButton className="sw-btn sw-btn--page" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pageCount}>
                Suivant
                <Icon icon="lucide:chevron-right" width={15} />
              </AppButton>
            </div>
          </div>
        )}
      </div>
      </div>
    </MasterLayout>
  );
};

export default SiteWebPage;
